// @ts-nocheck
/**
 * Recipient chips for share links. Each address becomes a chip on Enter, comma
 * or Tab; malformed addresses are rejected inline. Chips carry their send
 * state so you can tell who already has the link.
 */
import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { X, Check } from "lucide-react";

export const isEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

export default function RecipientChipsInput({
  value,
  onChange,
  sentMap = {},
  placeholder = "coach@club.com",
  disabled = false,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  sentMap?: Record<string, string>;
  placeholder?: string;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  const commit = (raw: string) => {
    const parts = raw
      .split(/[\s,;]+/)
      .map((p) => p.trim().toLowerCase())
      .filter(Boolean);
    if (parts.length === 0) return true;
    const bad = parts.find((p) => !isEmail(p));
    if (bad) {
      setError(`"${bad}" isn't a valid email address.`);
      return false;
    }
    const next = Array.from(new Set([...value, ...parts]));
    onChange(next);
    setDraft("");
    setError(null);
    return true;
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "," || e.key === "Tab") {
      if (!draft.trim()) return;
      e.preventDefault();
      commit(draft);
      return;
    }
    if (e.key === "Backspace" && !draft && value.length) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div className="space-y-1.5">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((email) => {
            const sentAt = sentMap[email];
            return (
              <span
                key={email}
                title={
                  sentAt
                    ? `Sent ${new Date(sentAt).toLocaleDateString()}`
                    : "Not sent yet"
                }
                className={`inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${
                  sentAt
                    ? "bg-muted text-muted-foreground"
                    : "border-primary/40 bg-primary/10 text-foreground"
                }`}
              >
                {sentAt ? (
                  <Check className="h-3 w-3 shrink-0 opacity-70" />
                ) : (
                  <span className="rounded-sm bg-primary/20 px-1 text-[10px] font-medium">
                    new
                  </span>
                )}
                <span className="truncate">{email}</span>
                {!disabled && (
                  <button
                    type="button"
                    aria-label={`Remove ${email}`}
                    className="rounded-full p-0.5 hover:bg-foreground/10"
                    onClick={() => onChange(value.filter((v) => v !== email))}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}
      <Input
        value={draft}
        disabled={disabled}
        onChange={(e) => {
          setDraft(e.target.value);
          if (error) setError(null);
        }}
        onKeyDown={onKeyDown}
        onBlur={() => draft.trim() && commit(draft)}
        placeholder={placeholder}
      />
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : (
        <p className="text-xs text-muted-foreground">
          Press Enter or comma after each address.
        </p>
      )}
    </div>
  );
}
